import type { GoalPriority, GoalStatus, GoalTargetType } from '@pms/shared';
import { formatINR } from '@/lib/currency';

interface PillMeta {
  label: string;
  /** Tailwind pill classes, light + dark. */
  className: string;
  /** Solid hex for dots, bars and swatches. */
  hex: string;
}

export const GOAL_STATUS_META: Record<GoalStatus, PillMeta & { description: string }> = {
  draft:     { label: 'Draft',     className: 'bg-slate-200 text-slate-600 dark:bg-slate-500/15 dark:text-slate-400',       hex: '#64748B', description: 'Planned but not started yet' },
  active:    { label: 'Active',    className: 'bg-teal-100 text-teal-700 dark:bg-teal-500/15 dark:text-teal-400',           hex: '#0D9488', description: 'In progress — log updates against it' },
  submitted: { label: 'In Review', className: 'bg-violet-100 text-violet-700 dark:bg-violet-500/15 dark:text-violet-400',   hex: '#8B5CF6', description: 'Submitted and waiting on a manager review' },
  achieved:  { label: 'Achieved',  className: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-400', hex: '#10B981', description: 'Reviewed and approved as complete' },
  missed:    { label: 'Missed',    className: 'bg-rose-100 text-rose-700 dark:bg-rose-500/15 dark:text-rose-400',           hex: '#EF4444', description: 'The deadline passed before completion' },
  cancelled: { label: 'Cancelled', className: 'bg-slate-200 text-slate-600 dark:bg-slate-500/15 dark:text-slate-400',       hex: '#94A3B8', description: 'Dropped — no longer being tracked' },
};

export const ALL_GOAL_STATUSES: GoalStatus[] = [
  'draft', 'active', 'submitted', 'achieved', 'missed', 'cancelled',
];

export const GOAL_TARGET_TYPE_META: Record<GoalTargetType, PillMeta & { hint: string }> = {
  numeric:    { label: 'Number',    className: 'bg-sky-100 text-sky-700 dark:bg-sky-500/15 dark:text-sky-400',           hex: '#0EA5E9', hint: 'A countable target, e.g. 1,000,000 followers' },
  percentage: { label: 'Percent',   className: 'bg-violet-100 text-violet-700 dark:bg-violet-500/15 dark:text-violet-400', hex: '#8B5CF6', hint: 'A rate or share between 0 and 100' },
  currency:   { label: 'Amount',    className: 'bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-400',   hex: '#F59E0B', hint: 'A rupee value, e.g. a sales target' },
  milestone:  { label: 'Milestone', className: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-400', hex: '#10B981', hint: 'A checklist — progress is ticked items' },
};

export const ALL_GOAL_TARGET_TYPES: GoalTargetType[] = ['numeric', 'percentage', 'currency', 'milestone'];

export const GOAL_PRIORITY_META: Record<GoalPriority, PillMeta> = {
  low:    { label: 'Low',    className: 'bg-slate-200 text-slate-600 dark:bg-slate-500/15 dark:text-slate-400', hex: '#94A3B8' },
  medium: { label: 'Medium', className: 'bg-sky-100 text-sky-700 dark:bg-sky-500/15 dark:text-sky-400',         hex: '#0EA5E9' },
  high:   { label: 'High',   className: 'bg-rose-100 text-rose-700 dark:bg-rose-500/15 dark:text-rose-400',     hex: '#EF4444' },
};

export const ALL_GOAL_PRIORITIES: GoalPriority[] = ['low', 'medium', 'high'];

/** Statuses a goal can still be worked on in. */
export const OPEN_GOAL_STATUSES: GoalStatus[] = ['draft', 'active', 'submitted'];

/**
 * Statuses in which progress can be recorded — mirrors the backend rule. `submitted`
 * is included so work added while a goal is under review can still be ticked off.
 */
export const PROGRESS_EDITABLE_STATUSES: GoalStatus[] = ['active', 'submitted'];

/** Why the progress controls are locked, for a tooltip on the disabled control. */
export function progressLockReason(status: GoalStatus): string | null {
  if (PROGRESS_EDITABLE_STATUSES.includes(status)) return null;
  if (status === 'draft') return 'Start this goal before recording progress';
  if (status === 'missed') return 'Reopen this goal before recording progress';
  return `This goal is ${GOAL_STATUS_META[status].label.toLowerCase()} — progress can no longer be changed`;
}

/** Radix Select rejects an empty string value, so filters use these sentinels. */
export const ALL_OPTION = '__all__';
export const NONE_OPTION = '__none__';

/** Compact number rendering so 1,200,000 doesn't blow up a card: "12L", "1.2Cr". */
function formatCompactNumber(value: number): string {
  const abs = Math.abs(value);
  if (abs >= 10_000_000) return `${(value / 10_000_000).toFixed(abs % 10_000_000 === 0 ? 0 : 2)}Cr`;
  if (abs >= 100_000) return `${(value / 100_000).toFixed(abs % 100_000 === 0 ? 0 : 2)}L`;
  if (abs >= 1_000) return `${(value / 1_000).toFixed(abs % 1_000 === 0 ? 0 : 1)}K`;
  return value.toLocaleString('en-IN', { maximumFractionDigits: 2 });
}

/** One value rendered the way its target type reads. */
export function formatGoalValue(
  value: number | null,
  targetType: GoalTargetType,
  unit: string | null,
  compact = false,
): string {
  if (value === null) return '—';
  if (targetType === 'currency') {
    return compact ? `₹${formatCompactNumber(value)}` : formatINR(value);
  }
  if (targetType === 'percentage') return `${value}%`;
  const shown = compact ? formatCompactNumber(value) : value.toLocaleString('en-IN', { maximumFractionDigits: 2 });
  return unit ? `${shown} ${unit}` : shown;
}

/**
 * A history entry's signed movement: "+₹1,00,000", "−12.5 kg". The sign is stripped before
 * formatting so it never depends on how the currency formatter renders negatives.
 */
export function formatGoalDelta(
  value: number,
  targetType: GoalTargetType,
  unit: string | null,
): string {
  return `${value < 0 ? '−' : '+'}${formatGoalValue(Math.abs(value), targetType, unit)}`;
}

/** "820K / 1M followers" or "3 / 8 milestones" — the caption under a progress bar. */
export function formatGoalProgressCaption(goal: {
  target_type: GoalTargetType;
  current_value: number;
  target_value: number | null;
  unit: string | null;
  milestone_done: number;
  milestone_total: number;
}): string {
  if (goal.target_type === 'milestone') {
    return `${goal.milestone_done} / ${goal.milestone_total} milestone${goal.milestone_total === 1 ? '' : 's'}`;
  }
  const current = formatGoalValue(goal.current_value, goal.target_type, null, true);
  const target = formatGoalValue(goal.target_value, goal.target_type, goal.unit, true);
  return `${current} / ${target}`;
}

export type GoalProgressTone = 'complete' | 'ontrack' | 'atrisk' | 'overdue' | 'closed';

/** Picks the bar colour from the goal's real state, not just its percentage. */
export function goalProgressTone(goal: {
  status: GoalStatus;
  progress_percent: number;
  is_overdue: boolean;
  is_at_risk: boolean;
}): GoalProgressTone {
  if (goal.status === 'achieved' || goal.progress_percent >= 100) return 'complete';
  if (goal.status === 'cancelled') return 'closed';
  if (goal.status === 'missed' || goal.is_overdue) return 'overdue';
  if (goal.is_at_risk) return 'atrisk';
  return 'ontrack';
}

export const GOAL_PROGRESS_TONE_CLASS: Record<GoalProgressTone, string> = {
  complete: 'bg-emerald-500',
  ontrack:  'bg-teal-500',
  atrisk:   'bg-amber-500',
  overdue:  'bg-rose-500',
  closed:   'bg-slate-400',
};

/** Whole days until the due date; negative once it has passed. */
export function daysUntil(dueDate: string): number {
  const MS_PER_DAY = 86_400_000;
  const due = new Date(`${dueDate}T00:00:00`);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.round((due.getTime() - today.getTime()) / MS_PER_DAY);
}

/** "Due in 12 days" / "Due tomorrow" / "6 days overdue". */
export function formatDueLabel(dueDate: string, status: GoalStatus): string {
  if (!OPEN_GOAL_STATUSES.includes(status)) return '';
  const days = daysUntil(dueDate);
  if (days < -1) return `${Math.abs(days)} days overdue`;
  if (days === -1) return 'Overdue by a day';
  if (days === 0) return 'Due today';
  if (days === 1) return 'Due tomorrow';
  return `Due in ${days} days`;
}
